/**
 * WebAuthn Utilities for Cross-Device Biometric Authentication
 * 
 * Supports:
 * - iPhone Face ID / Touch ID (via iCloud Keychain passkeys)
 * - Cross-device authentication (scan QR code with phone)
 * - Platform authenticators (Windows Hello, macOS Touch ID)
 * 
 * SECURITY: Credentials are hardware-bound and cannot be stolen
 */

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

export interface WebAuthnConfig {
  rpId: string;
  rpName: string;
  origin: string;
}

export interface RegistrationResult {
  credentialId: string;
  publicKey: string;
  counter: number;
  transports: string[];
  authenticatorType: "platform" | "cross-platform";
  userVerified: boolean;
}

export interface AuthenticationResult {
  credentialId: string;
  newCounter: number;
  userVerified: boolean;
  signature: string;
}

// =============================================================================
// BASE64URL ENCODING/DECODING
// =============================================================================

export function base64urlToArrayBuffer(base64url: string): ArrayBuffer {
  // Add padding if needed
  const padding = "=".repeat((4 - (base64url.length % 4)) % 4);
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/") + padding;
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export function arrayBufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  const base64 = btoa(binary);
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// =============================================================================
// CHALLENGE GENERATION
// =============================================================================

export function generateChallenge(): string {
  const challenge = new Uint8Array(32);
  crypto.getRandomValues(challenge);
  return arrayBufferToBase64url(challenge.buffer);
}

// =============================================================================
// REGISTRATION (CREATE PASSKEY)
// =============================================================================

export interface CreatePasskeyOptions {
  config: WebAuthnConfig;
  userId: string;
  userName: string;
  displayName: string;
  challenge: string;
  
  // Set to true to specifically request iPhone/phone authentication
  preferCrossDevice?: boolean;
  
  // Require biometric verification (Face ID, Touch ID, fingerprint)
  requireBiometric?: boolean;
  
  // Existing credential IDs to exclude
  excludeCredentials?: string[];
}

/**
 * Create passkey registration options
 * Optimized for iPhone Face ID / Touch ID with cross-device support
 */
export function createRegistrationOptions(options: CreatePasskeyOptions): PublicKeyCredentialCreationOptions {
  const {
    config,
    userId,
    userName,
    displayName,
    challenge,
    preferCrossDevice = false,
    requireBiometric = true,
    excludeCredentials = [],
  } = options;

  // Convert excluded credentials
  const excludeList: PublicKeyCredentialDescriptor[] = excludeCredentials.map((id) => ({
    type: "public-key" as const,
    id: base64urlToArrayBuffer(id),
    // Include all transports for maximum compatibility
    transports: ["internal", "hybrid", "usb", "ble", "nfc"] as AuthenticatorTransport[],
  }));

  return {
    challenge: base64urlToArrayBuffer(challenge),
    
    rp: {
      id: config.rpId,
      name: config.rpName,
    },
    
    user: {
      id: new TextEncoder().encode(userId),
      name: userName,
      displayName: displayName,
    },
    
    // Preferred algorithms (ES256 is most widely supported)
    pubKeyCredParams: [
      { type: "public-key", alg: -7 },   // ES256 (ECDSA with P-256)
      { type: "public-key", alg: -257 }, // RS256 (RSASSA-PKCS1-v1_5)
    ],
    
    timeout: 120000, // 2 minutes for cross-device
    
    authenticatorSelection: {
      // "cross-platform" allows phone authenticators (iPhone)
      // "platform" only allows built-in (Touch Bar, Windows Hello)
      // undefined allows both
      ...(preferCrossDevice && { authenticatorAttachment: "cross-platform" as const }),
      
      // Require the credential to be stored on the device (passkey)
      residentKey: "required",
      requireResidentKey: true,
      
      // CRITICAL: Require user verification (biometric or PIN)
      // "required" = Face ID, Touch ID, fingerprint, or PIN MUST be used
      userVerification: requireBiometric ? "required" : "preferred",
    },
    
    excludeCredentials: excludeList,
    
    // Request attestation for verification
    attestation: "direct",
    
    // Extensions for additional features
    extensions: {
      // Request credential properties
      credProps: true,
    },
  };
}

/**
 * Process registration response
 */
export function processRegistrationResponse(
  credential: PublicKeyCredential
): RegistrationResult {
  const response = credential.response as AuthenticatorAttestationResponse;
  
  // Get transports (important for cross-device)
  let transports: string[] = [];
  if ("getTransports" in response) {
    transports = (response as any).getTransports() || [];
  }
  
  // Determine authenticator type
  const authenticatorType = transports.includes("internal") ? "platform" : "cross-platform";
  
  // Parse attestation object to get public key
  // Note: attestationObject contains the public key which we extract for storage
  const _attestationObject = new Uint8Array(response.attestationObject);
  void _attestationObject; // Available for advanced CBOR parsing if needed
  
  return {
    credentialId: arrayBufferToBase64url(credential.rawId),
    publicKey: arrayBufferToBase64url(response.attestationObject),
    counter: 0, // Initial counter
    transports,
    authenticatorType,
    userVerified: true, // If we got here with UV required, user was verified
  };
}

// =============================================================================
// AUTHENTICATION (SIGN IN WITH PASSKEY)
// =============================================================================

export interface AuthenticateOptions {
  config: WebAuthnConfig;
  challenge: string;
  
  // Specific credential IDs to allow (empty = any registered credential)
  allowCredentials?: Array<{
    id: string;
    transports?: string[];
  }>;
  
  // Require biometric verification
  requireBiometric?: boolean;
  
  // Prefer cross-device (show QR code for phone)
  preferCrossDevice?: boolean;
}

/**
 * Create authentication options
 * Optimized for cross-device iPhone authentication
 */
export function createAuthenticationOptions(options: AuthenticateOptions): PublicKeyCredentialRequestOptions {
  const {
    config,
    challenge,
    allowCredentials = [],
    requireBiometric = true,
    preferCrossDevice = false,
  } = options;

  // Build allowed credentials list
  let allowList: PublicKeyCredentialDescriptor[] | undefined;
  
  if (allowCredentials.length > 0) {
    allowList = allowCredentials.map((cred) => ({
      type: "public-key" as const,
      id: base64urlToArrayBuffer(cred.id),
      // Include "hybrid" for cross-device authentication with phones
      transports: (cred.transports || ["internal", "hybrid", "usb", "ble", "nfc"]) as AuthenticatorTransport[],
    }));
  } else if (preferCrossDevice) {
    // If preferring cross-device with no specific credentials,
    // the browser will show discoverable credentials including phones
    allowList = undefined;
  }

  return {
    ...(preferCrossDevice && {
      hints: ["hybrid"], // Hint to prefer cross-device authentication
    }),
    challenge: base64urlToArrayBuffer(challenge),
    
    rpId: config.rpId,
    
    timeout: 120000, // 2 minutes for cross-device
    
    ...(allowList && { allowCredentials: allowList }),
    
    // CRITICAL: Require user verification for creator authority
    userVerification: requireBiometric ? "required" : "preferred",
  };
}

/**
 * Process authentication response
 */
export function processAuthenticationResponse(
  credential: PublicKeyCredential
): AuthenticationResult {
  const response = credential.response as AuthenticatorAssertionResponse;
  
  // Parse authenticator data to get counter
  const authData = new Uint8Array(response.authenticatorData);
  
  // Counter is bytes 33-36 (big-endian)
  const counter = (authData[33]! << 24) | (authData[34]! << 16) | (authData[35]! << 8) | authData[36]!;
  
  // Check user verification flag (bit 2 of flags byte)
  const flags = authData[32]!;
  const userVerified = (flags & 0x04) !== 0;
  
  return {
    credentialId: arrayBufferToBase64url(credential.rawId),
    newCounter: counter,
    userVerified,
    signature: arrayBufferToBase64url(response.signature),
  };
}

// =============================================================================
// CROSS-DEVICE AUTHENTICATION FLOW
// =============================================================================

/**
 * Start cross-device authentication
 * This will show a QR code that can be scanned with iPhone
 * The iPhone will then prompt for Face ID / Touch ID
 */
export async function authenticateWithPhone(
  config: WebAuthnConfig,
  challenge: string
): Promise<AuthenticationResult> {
  const options = createAuthenticationOptions({
    config,
    challenge,
    requireBiometric: true,
    preferCrossDevice: true,
    allowCredentials: [], // Allow any discoverable credential
  });

  // This will trigger the browser's WebAuthn UI
  // Modern browsers will show:
  // 1. Option to use local passkey (if available)
  // 2. QR code for cross-device authentication
  // 3. When iPhone scans QR, it sends push notification
  // 4. User authenticates with Face ID / Touch ID on iPhone
  // 5. Authentication completes on original device
  
  const credential = await navigator.credentials.get({
    publicKey: options,
    // Mediation: "optional" allows browser to remember choice
    mediation: "optional",
  }) as PublicKeyCredential | null;

  if (!credential) {
    throw new Error("Authentication cancelled or failed");
  }

  return processAuthenticationResponse(credential);
}

/**
 * Check if WebAuthn is supported
 */
export function isWebAuthnSupported(): boolean {
  return !!(
    window.PublicKeyCredential &&
    typeof window.PublicKeyCredential === "function"
  );
}

/**
 * Check if platform authenticator is available (Touch ID, Face ID, Windows Hello)
 */
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported()) return false;
  
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/**
 * Check if conditional mediation is supported (autofill passkeys)
 */
export async function isConditionalMediationAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported()) return false;
  
  try {
    return await (PublicKeyCredential as any).isConditionalMediationAvailable?.() ?? false;
  } catch {
    return false;
  }
}

// =============================================================================
// CREATOR AUTHORITY SPECIFIC
// =============================================================================

/**
 * Register as Creator with biometric authentication
 * This creates an unstealable credential bound to your hardware
 */
export async function registerCreatorCredential(
  config: WebAuthnConfig,
  userId: string,
  displayName: string
): Promise<RegistrationResult> {
  const challenge = generateChallenge();
  
  const options = createRegistrationOptions({
    config,
    userId,
    userName: `creator@${config.rpId}`,
    displayName,
    challenge,
    preferCrossDevice: false, // Allow both platform and cross-platform
    requireBiometric: true,   // MUST use biometric
  });

  const credential = await navigator.credentials.create({
    publicKey: options,
  }) as PublicKeyCredential | null;

  if (!credential) {
    throw new Error("Registration cancelled or failed");
  }

  return processRegistrationResponse(credential);
}

/**
 * Authenticate as Creator with biometric
 * Can use iPhone Face ID / Touch ID even from another device
 */
export async function authenticateCreator(
  config: WebAuthnConfig,
  allowCredentials: Array<{ id: string; transports?: string[] }> = []
): Promise<AuthenticationResult> {
  const challenge = generateChallenge();
  
  const options = createAuthenticationOptions({
    config,
    challenge,
    allowCredentials,
    requireBiometric: true,
    preferCrossDevice: allowCredentials.length === 0, // If no specific creds, allow phone
  });

  const credential = await navigator.credentials.get({
    publicKey: options,
    mediation: "optional",
  }) as PublicKeyCredential | null;

  if (!credential) {
    throw new Error("Authentication cancelled or failed");
  }

  return processAuthenticationResponse(credential);
}

// =============================================================================
// JSON SERIALIZATION FOR API CALLS
// =============================================================================

export interface RegistrationJSON {
  id: string;
  rawId: string;
  type: string;
  response: {
    attestationObject: string;
    clientDataJSON: string;
    transports?: string[];
  };
  clientExtensionResults: Record<string, unknown>;
}

export interface AuthenticationJSON {
  id: string;
  rawId: string;
  type: string;
  response: {
    authenticatorData: string;
    clientDataJSON: string;
    signature: string;
    userHandle: string | null;
  };
  clientExtensionResults: Record<string, unknown>;
}

/**
 * Convert registration credential to JSON for API transmission
 */
export function credentialToRegistrationJSON(credential: PublicKeyCredential): RegistrationJSON {
  const response = credential.response as AuthenticatorAttestationResponse;
  
  return {
    id: credential.id,
    rawId: arrayBufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: arrayBufferToBase64url(response.attestationObject),
      clientDataJSON: arrayBufferToBase64url(response.clientDataJSON),
      transports: "getTransports" in response ? (response as any).getTransports() : undefined,
    },
    clientExtensionResults: credential.getClientExtensionResults() as Record<string, unknown>,
  };
}

/**
 * Convert authentication credential to JSON for API transmission
 */
export function credentialToAuthenticationJSON(credential: PublicKeyCredential): AuthenticationJSON {
  const response = credential.response as AuthenticatorAssertionResponse;
  
  return {
    id: credential.id,
    rawId: arrayBufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      authenticatorData: arrayBufferToBase64url(response.authenticatorData),
      clientDataJSON: arrayBufferToBase64url(response.clientDataJSON),
      signature: arrayBufferToBase64url(response.signature),
      userHandle: response.userHandle ? arrayBufferToBase64url(response.userHandle) : null,
    },
    clientExtensionResults: credential.getClientExtensionResults() as Record<string, unknown>,
  };
}
